import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { shootService } from '../services/shootService'
import { availabilityService } from '../services/availabilityService'
import { downloadICSFile, formatDateForApi } from '../utils/calendarExport'
import { Shoot } from '../types'
import { AvailabilityCheck, TimeOffPeriod } from '../types/availability'
import AvailabilityManager from '../components/AvailabilityManager'
import SmartSchedulingSuggestions from '../components/SmartSchedulingSuggestions'
import AvailabilitySetupModal from '../components/AvailabilitySetupModal'
import TimeOffManager from '../components/TimeOffManager'
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  CameraIcon,
  VideoCameraIcon,
  ClockIcon,
  MapPinIcon,
  CogIcon,
  SparklesIcon,
  PlusIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  NoSymbolIcon
} from '@heroicons/react/24/outline'

const CalendarPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  
  const [shoots, setShoots] = useState<Shoot[]>([])
  const [loading, setLoading] = useState(true)
  const [currentDate, setCurrentDate] = useState(new Date())
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_selectedShoot, setSelectedShoot] = useState<Shoot | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  
  // New advanced features state
  const [showAvailabilityManager, setShowAvailabilityManager] = useState(false)
  const [showSmartSuggestions, setShowSmartSuggestions] = useState(false)
  const [showAvailabilitySetup, setShowAvailabilitySetup] = useState(false)
  const [showTimeOffManager, setShowTimeOffManager] = useState(false)
  const [availabilityCheck, setAvailabilityCheck] = useState<AvailabilityCheck | null>(null)
  const [timeOffPeriods, setTimeOffPeriods] = useState<TimeOffPeriod[]>([])
  const [calendarView, setCalendarView] = useState<'month' | 'week' | 'availability'>('month')

  const isPremium = user?.subscriptionStatus === 'professional' || user?.subscriptionStatus === 'studio'

  useEffect(() => {
    if (user?.id) {
      loadShoots()
      loadTimeOffPeriods()
      // Clear any cached availability checks when the page loads
      setAvailabilityCheck(null)
    }
  }, [user?.id])

  useEffect(() => {
    if (user?.id && isPremium) {
      checkAvailabilitySystemStatus()
    }
  }, [user?.id, isPremium])

  const checkAvailabilitySystemStatus = async () => {
    try {
      await availabilityService.checkAvailabilityTablesExist()
    } catch (error) {
      console.error('Error checking availability system:', error)
    }
  }

  const loadShoots = async () => {
    try {
      setLoading(true)
      const shootsData = await shootService.getShootsForUser(user!.id)
      setShoots(shootsData)
    } catch (err) {
      console.error('Error loading shoots:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadTimeOffPeriods = async () => {
    try {
      if (!user?.id) return
      
      // Get time off for current month and surrounding months
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0)
      
      const periods = await availabilityService.getTimeOffPeriods(
        user.id,
        formatDateForApi(startOfMonth),
        formatDateForApi(endOfMonth)
      )
      setTimeOffPeriods(periods)
    } catch (error) {
      console.error('Error loading time off periods:', error)
    }
  }

  const handleDateClick = async (date: Date) => {
    setSelectedDate(date)
    setSelectedShoot(null)
    
    // Check availability for this date (premium feature)
    if (user?.subscriptionStatus === 'professional' || user?.subscriptionStatus === 'studio') {
      try {
        const dateString = formatDateForApi(date)
        const check = await availabilityService.checkAvailability(
          user.id,
          dateString,
          '09:00',
          '17:00'
        )
        setAvailabilityCheck(check)
      } catch (error) {
        console.error('Error checking availability:', error)
      }
    }
  }

  const handleSmartSuggestionSelect = (suggestion: any) => {
    // Navigate to new shoot page with pre-filled data
    navigate('/new-shoot', {
      state: {
        prefillData: {
          date: suggestion.date,
          start_time: suggestion.startTime,
          end_time: suggestion.endTime
        }
      }
    })
  }

  const handleExportCalendar = () => {
    const filename = `freelance-calendar-${formatDateForApi(new Date())}.ics`
    downloadICSFile(shoots, filename)
  }

  const handleAvailabilityManagerOpen = async () => {
    try {
      // Force a fresh check of availability tables
      console.log('Checking availability system...')
      
      const tablesExist = await availabilityService.checkAvailabilityTablesExist()
      console.log('Tables exist:', tablesExist)
      
      if (tablesExist) {
        // Try to set up the user's availability system
        const setupResult = await availabilityService.setupAvailabilitySystem(user!.id)
        console.log('Setup result:', setupResult)
        
        if (setupResult.success) {
          setShowAvailabilityManager(true)
        } else {
          console.log('Setup needed:', setupResult.message)
          setShowAvailabilitySetup(true)
        }
      } else {
        console.log('Tables do not exist, showing setup modal')
        setShowAvailabilitySetup(true)
      }
    } catch (error) {
      console.error('Error checking availability system:', error)
      setShowAvailabilitySetup(true)
    }
  }

  const handleTimeOffManagerOpen = async () => {
    try {
      // Check if availability tables exist
      const tablesExist = await availabilityService.checkAvailabilityTablesExist()
      
      if (tablesExist) {
        // Ensure user has basic availability setup
        const setupResult = await availabilityService.setupAvailabilitySystem(user!.id)
        if (setupResult.success) {
          setShowTimeOffManager(true)
        } else {
          setShowAvailabilitySetup(true)
        }
      } else {
        setShowAvailabilitySetup(true)
      }
    } catch (error) {
      console.error('Error opening time off manager:', error)
      setShowAvailabilitySetup(true)
    }
  }

  const handleAvailabilitySetupComplete = () => {
    setShowAvailabilitySetup(false)
    // Refresh the page state
    if (user?.id) {
      loadTimeOffPeriods()
    }
  }

  const handleTimeOffCreated = () => {
    // Refresh time off periods and shoots
    if (user?.id) {
      loadTimeOffPeriods()
      loadShoots()
    }
  }

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day))
    }
    
    return days
  }

  const getShootsForDate = (date: Date | null) => {
    if (!date) return []
    // Use local timezone date formatting to avoid timezone offset issues
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const dateString = `${year}-${month}-${day}`
    return shoots.filter(shoot => shoot.date === dateString)
  }

  const getTimeOffForDate = (date: Date | null) => {
    if (!date) return []
    const dateString = formatDateForApi(date)
    return timeOffPeriods.filter(period => 
      dateString >= period.startDate && dateString <= period.endDate
    )
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate)
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1)
    } else {
      newDate.setMonth(newDate.getMonth() + 1)
    }
    setCurrentDate(newDate)
    setSelectedDate(null)
    setAvailabilityCheck(null)
  }

  // Update time off periods when month changes
  useEffect(() => {
    if (user?.id) {
      loadTimeOffPeriods()
    }
  }, [currentDate])

  const days = getDaysInMonth(currentDate)
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const renderCalendarView = () => {
    switch (calendarView) {
      case 'month':
        return renderMonthView()
      case 'week':
        return renderWeekView()
      case 'availability':
        return renderAvailabilityView()
      default:
        return renderMonthView()
    }
  }

  const renderMonthView = () => (
    <>
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {dayNames.map((day) => (
          <div key={day} className="text-center text-gray-400 font-medium py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((day, index) => {
          if (!day) {
            return <div key={index} className="h-20"></div>
          }

          const dayShots = getShootsForDate(day)
          const dayTimeOff = getTimeOffForDate(day)
          const isToday = day.toDateString() === new Date().toDateString()
          const isSelected = selectedDate?.toDateString() === day.toDateString()
          const hasShots = dayShots.length > 0

          return (
            <motion.div
              key={index}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleDateClick(day)}
              className={`
                h-20 p-2 rounded-xl cursor-pointer transition-all relative
                ${isToday 
                  ? 'bg-blue-500/30 border-2 border-blue-500' 
                  : isSelected
                  ? 'bg-purple-500/20 border-2 border-purple-500'
                  : hasShots
                  ? 'bg-green-500/10 border border-green-500/30 hover:bg-green-500/20'
                  : 'bg-white/5 border border-white/10 hover:bg-white/10'
                }
              `}
            >
              <div className="flex justify-between items-start mb-1">
                <span className={`text-sm font-medium ${
                  isToday ? 'text-white' : isSelected ? 'text-purple-300' : 'text-gray-300'
                }`}>
                  {day.getDate()}
                </span>
                {isPremium && availabilityCheck && isSelected && (
                  <div className={`w-2 h-2 rounded-full ${
                    availabilityCheck.available ? 'bg-green-400' : 'bg-red-400'
                  }`} />
                )}
              </div>
              
              {/* Time Off Indicators */}
              {dayTimeOff.length > 0 && (
                <div className="space-y-1 mb-1">
                  {dayTimeOff.slice(0, 1).map((timeOff) => {
                    const typeColors = {
                      vacation: 'bg-blue-500',
                      sick: 'bg-red-500',
                      personal: 'bg-green-500',
                      holiday: 'bg-purple-500',
                      travel: 'bg-yellow-500',
                      family: 'bg-pink-500',
                      other: 'bg-gray-500'
                    }
                    
                    return (
                      <div
                        key={timeOff.id}
                        className={`text-xs p-1 rounded ${typeColors[timeOff.type]} text-white truncate`}
                      >
                        {timeOff.title}
                      </div>
                    )
                  })}
                  {dayTimeOff.length > 1 && (
                    <div className="text-xs text-gray-400">
                      +{dayTimeOff.length - 1} more
                    </div>
                  )}
                </div>
              )}

              {/* Shoots indicators */}
              <div className="space-y-1">
                {dayShots.slice(0, 2).map((shoot) => (
                  <div
                    key={shoot.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedShoot(shoot)
                    }}
                    className="text-xs p-1 rounded bg-white/20 text-white truncate hover:bg-white/30 transition-colors"
                  >
                    {shoot.title}
                  </div>
                ))}
                {dayShots.length > 2 && (
                  <div className="text-xs text-gray-400">
                    +{dayShots.length - 2} more
                  </div>
                )}
              </div>
            </motion.div>
          )
        })}
      </div>
    </>
  )

  const renderWeekView = () => (
    <div className="text-center py-12">
      <CalendarDaysIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
      <h3 className="text-white text-lg font-medium mb-2">Week View</h3>
      <p className="text-gray-400 mb-4">Coming soon! Week view will show your schedule in a weekly format.</p>
      <button
        onClick={() => setCalendarView('month')}
        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
      >
        Back to Month View
      </button>
    </div>
  )

  const renderAvailabilityView = () => (
    <div className="text-center py-12">
      <ClockIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
      <h3 className="text-white text-lg font-medium mb-2">Availability View</h3>
      <p className="text-gray-400 mb-4">This view will show your availability patterns and working hours.</p>
      <div className="space-y-3">
        <button
          onClick={handleAvailabilityManagerOpen}
          className="block mx-auto px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
        >
          Manage Availability
        </button>
        <button
          onClick={() => setCalendarView('month')}
          className="block mx-auto px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Back to Month View
        </button>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
        </div>
        <div className="glass-mockup rounded-3xl p-8">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-white mt-4">Loading calendar...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-3 text-white hover:text-gray-300 transition-colors"
            >
              <ArrowLeftIcon className="w-6 h-6" />
              <span className="text-lg font-medium">Back to Dashboard</span>
            </motion.button>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-3"
            >
              <div className="w-12 h-12 glass-badge rounded-2xl flex items-center justify-center">
                <CalendarDaysIcon className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">
                Advanced Calendar {isPremium && <span className="text-purple-400">Pro</span>}
              </span>
            </motion.div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Premium Features Toolbar */}
        {isPremium && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex flex-wrap items-center gap-3 p-4 glass-mockup rounded-2xl"
          >
            <div className="flex items-center space-x-2 text-white">
              <SparklesIcon className="w-5 h-5 text-purple-400" />
              <span className="font-medium">Premium Features:</span>
            </div>
            
            <button
              onClick={handleAvailabilityManagerOpen}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
            >
              <CogIcon className="w-4 h-4" />
              <span>Availability Settings</span>
            </button>

            <button
              onClick={handleTimeOffManagerOpen}
              className="flex items-center space-x-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
            >
              <NoSymbolIcon className="w-4 h-4" />
              <span>Time Off</span>
            </button>

            <button
              onClick={() => setShowSmartSuggestions(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg transition-colors"
            >
              <SparklesIcon className="w-4 h-4" />
              <span>Smart Scheduling</span>
            </button>

            <button
              onClick={() => navigate('/new-shoot')}
              className="flex items-center space-x-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              <span>New Shoot</span>
            </button>

            <button
              onClick={handleExportCalendar}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-lg transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span>Export Calendar</span>
            </button>

            <div className="ml-auto flex items-center space-x-2">
              <span className="text-gray-400 text-sm">View:</span>
              {['month', 'week', 'availability'].map((view) => (
                <button
                  key={view}
                  onClick={() => setCalendarView(view as any)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    calendarView === view
                      ? 'bg-white/20 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {view.charAt(0).toUpperCase() + view.slice(1)}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Upcoming: Advanced Calendar Integration */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <CalendarDaysIcon className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-bold text-white">Upcoming: Advanced Calendar Integration</h2>
          </div>
          <div className="glass-badge rounded-xl p-4 text-white/80 bg-blue-900/30 border-l-4 border-blue-500 mb-2">
            <strong>Google/Outlook Sync</strong> and <strong>Weather Conflict Detection</strong> coming soon! Seamlessly sync your shoots with external calendars and get real-time weather alerts for scheduled events.
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-mockup rounded-3xl p-6"
            >
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <div className="flex items-center space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => navigateMonth('prev')}
                    className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setCurrentDate(new Date())}
                    className="px-4 py-2 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 font-medium transition-colors"
                  >
                    Today
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => navigateMonth('next')}
                    className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>

              {renderCalendarView()}
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Selected Date Info */}
            {selectedDate && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="glass-mockup rounded-2xl p-6"
              >
                <h3 className="text-lg font-semibold text-white mb-4">
                  {selectedDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h3>

                {/* Time Off for Selected Date */}
                {getTimeOffForDate(selectedDate).length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-400 mb-2">Time Off</h4>
                    <div className="space-y-2">
                      {getTimeOffForDate(selectedDate).map((timeOff) => (
                        <div key={timeOff.id} className="p-2 bg-red-500/10 rounded-lg border border-red-500/20">
                          <div className="font-medium text-red-400">{timeOff.title}</div>
                          {timeOff.reason && (
                            <div className="text-sm text-gray-400">{timeOff.reason}</div>
                          )}
                          {timeOff.startTime && timeOff.endTime && (
                            <div className="text-xs text-gray-500">
                              {formatTime(timeOff.startTime)} - {formatTime(timeOff.endTime)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Shoots for selected date */}
                <div className="space-y-3">
                  {getShootsForDate(selectedDate).length > 0 ? (
                    getShootsForDate(selectedDate).map(shoot => (
                      <div 
                        key={shoot.id} 
                        onClick={() => setSelectedShoot(shoot)}
                        className="p-4 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors border border-white/10"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            {shoot.type === 'photo' ? (
                              <CameraIcon className="w-5 h-5 text-blue-400 flex-shrink-0" />
                            ) : (
                              <VideoCameraIcon className="w-5 h-5 text-purple-400 flex-shrink-0" />
                            )}
                            <span className="text-white font-medium text-sm">{shoot.title}</span>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            shoot.type === 'photo' 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {shoot.type === 'photo' ? 'Photo' : 'Video'}
                          </span>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2 text-sm text-gray-300">
                            <ClockIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span>{formatTime(shoot.start_time)} - {formatTime(shoot.end_time)}</span>
                          </div>
                          
                          {shoot.location && (
                            <div className="flex items-center space-x-2 text-sm text-gray-300">
                              <MapPinIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{shoot.location}</span>
                            </div>
                          )}
                          
                          {shoot.client && (
                            <div className="flex items-center space-x-2 text-sm text-gray-300">
                              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span className="truncate">{shoot.client.name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6">
                      <div className="w-12 h-12 rounded-full bg-gray-500/10 flex items-center justify-center mx-auto mb-3">
                        <CalendarDaysIcon className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-gray-400 mb-4">No shoots scheduled for this day</p>
                      <div className="space-y-2">
                        <button
                          onClick={() => navigate('/new-shoot')}
                          className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors text-sm"
                        >
                          <PlusIcon className="w-4 h-4" />
                          <span>Schedule New Shoot</span>
                        </button>
                        {isPremium && (
                          <>
                            <button
                              onClick={() => setShowSmartSuggestions(true)}
                              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg transition-colors text-sm"
                            >
                              <SparklesIcon className="w-4 h-4" />
                              <span>Smart Suggestions</span>
                            </button>
                            <button
                              onClick={() => setShowTimeOffManager(true)}
                              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors text-sm"
                            >
                              <NoSymbolIcon className="w-4 h-4" />
                              <span>Schedule Time Off</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-mockup rounded-2xl p-6"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/new-shoot')}
                  className="w-full flex items-center space-x-3 p-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
                >
                  <PlusIcon className="w-5 h-5" />
                  <span>Schedule New Shoot</span>
                </button>

                {isPremium && (
                  <>
                    <button
                      onClick={() => setShowSmartSuggestions(true)}
                      className="w-full flex items-center space-x-3 p-3 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg transition-colors"
                    >
                      <SparklesIcon className="w-5 h-5" />
                      <span>Smart Suggestions</span>
                    </button>

                    <button
                      onClick={handleAvailabilityManagerOpen}
                      className="w-full flex items-center space-x-3 p-3 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
                    >
                      <CogIcon className="w-5 h-5" />
                      <span>Manage Availability</span>
                    </button>
                  </>
                )}

                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full flex items-center space-x-3 p-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  <EyeIcon className="w-5 h-5" />
                  <span>View Dashboard</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Modals */}
      {isPremium && (
        <>
          <AvailabilityManager
            isOpen={showAvailabilityManager}
            onClose={() => setShowAvailabilityManager(false)}
          />

          <SmartSchedulingSuggestions
            isOpen={showSmartSuggestions}
            onClose={() => setShowSmartSuggestions(false)}
            onSelectSuggestion={handleSmartSuggestionSelect}
            preferredDate={selectedDate ? formatDateForApi(selectedDate) : undefined}
          />

          <AvailabilitySetupModal
            isOpen={showAvailabilitySetup}
            onClose={() => setShowAvailabilitySetup(false)}
            onSetupComplete={handleAvailabilitySetupComplete}
          />

          <TimeOffManager
            isOpen={showTimeOffManager}
            onClose={() => setShowTimeOffManager(false)}
            selectedDate={selectedDate ? formatDateForApi(selectedDate) : undefined}
            onTimeOffCreated={handleTimeOffCreated}
          />
        </>
      )}
    </div>
  )
}

export default CalendarPage 